import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
} from "react";
import { useSocket } from "./SocketContext";
import { audiofile } from "../assets/images/index";

const CallContext = createContext(null);

export const CallProvider = ({ children }) => {
  const { socket, getCurrentUserId } = useSocket();
  const [incomingCall, setIncomingCall] = useState(null);
  const [activeCall, setActiveCall] = useState(null);
  const [callStatus, setCallStatus] = useState("idle");
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const activeCallRef = useRef(null);

  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const ringtoneRef = useRef(null);
  const pendingIceCandidatesRef = useRef([]);
  const remoteDescriptionSetRef = useRef(false);
  const remoteAudioRef = useRef(null); // Reference to audio element

  useEffect(() => {
    activeCallRef.current = activeCall;
  }, [activeCall]);

  const ICE_SERVERS = {
    iceServers: [
      { urls: "stun:stun.l.google.com:19302" },
      { urls: "stun:stun1.l.google.com:19302" },
      { urls: "stun:stun2.l.google.com:19302" },
      {
        urls: "turn:openrelay.metered.ca:80",
        username: "openrelayproject",
        credential: "openrelayproject",
      },
      {
        urls: "turn:openrelay.metered.ca:443",
        username: "openrelayproject",
        credential: "openrelayproject",
      },
      {
        urls: "turn:openrelay.metered.ca:443?transport=tcp",
        username: "openrelayproject",
        credential: "openrelayproject",
      },
    ],
    iceCandidatePoolSize: 10,
  };

  const log = (symbol, ...args) =>
    console.log(symbol, "[CallContext]", ...args);

  // 🔔 Show browser notification
  const showCallNotification = (caller, type) => {
    if (!("Notification" in window)) return;
    if (Notification.permission === "granted") {
      const notification = new Notification(
        `Incoming ${type === "video" ? "Video" : "Voice"} Call`,
        {
          body: `${caller.username} is calling you`,
          icon: caller.avatar || "/phone-icon.png",
          tag: "incoming-call",
          requireInteraction: true,
        }
      );
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    } else if (Notification.permission === "default") {
      Notification.requestPermission();
    }
  };

  // 🎵 Ringtone
  const playRingtone = () => {
    try {
      const audio = new Audio(audiofile);
      audio.loop = true;
      audio.volume = 0.5;
      audio.play().catch((err) => log("⚠️", "Ringtone play failed", err));
      ringtoneRef.current = audio;
    } catch (e) {
      log("❌", "Ringtone error", e);
    }
  };

  const stopRingtone = () => {
    if (ringtoneRef.current) {
      ringtoneRef.current.pause?.();
      ringtoneRef.current.currentTime = 0;
      ringtoneRef.current = null;
    }
  };

  // 🔊 Setup audio playback
  const setupRemoteAudio = (stream) => {
    log("🔊", "Setting up remote audio playback");

    // Create or reuse audio element
    if (!remoteAudioRef.current) {
      remoteAudioRef.current = document.createElement("audio");
      remoteAudioRef.current.autoplay = true;
      remoteAudioRef.current.playsInline = true;
      document.body.appendChild(remoteAudioRef.current);
      log("✅", "Created new audio element");
    }

    // Set the stream
    remoteAudioRef.current.srcObject = stream;
    remoteAudioRef.current.volume = 1.0;

    // Force play (important for mobile)
    remoteAudioRef.current
      .play()
      .then(() => {
        log("✅", "Remote audio playing");
      })
      .catch((err) => {
        log("❌", "Failed to play remote audio:", err);
        // Retry after user interaction
        setTimeout(() => {
          remoteAudioRef.current
            ?.play()
            .catch((e) => log("❌", "Retry play failed:", e));
        }, 500);
      });

    // Log audio tracks
    const audioTracks = stream.getAudioTracks();
    audioTracks.forEach((track) => {
      log(
        "🎵",
        `Audio track: enabled=${track.enabled}, muted=${track.muted}, readyState=${track.readyState}`
      );
    });
  };

  // 🧊 Process Pending ICE Candidates
  const processPendingIceCandidates = async () => {
    const pc = peerConnectionRef.current;
    if (
      !pc ||
      !remoteDescriptionSetRef.current ||
      pendingIceCandidatesRef.current.length === 0
    ) {
      return;
    }

    log(
      "🧊",
      `Processing ${pendingIceCandidatesRef.current.length} pending ICE candidates`
    );

    const candidates = [...pendingIceCandidatesRef.current];
    pendingIceCandidatesRef.current = [];

    for (const candidate of candidates) {
      try {
        await pc.addIceCandidate(new RTCIceCandidate(candidate));
        log("✅", "Added buffered ICE candidate");
      } catch (e) {
        log("⚠️", "Error adding buffered ICE:", e);
      }
    }
  };

  // 🎥 Create Peer Connection
  const createPeerConnection = (callId, targetUserId, isInitiator) => {
    log(
      "📞",
      `Creating peer connection for call: ${callId}, isInitiator: ${isInitiator}`
    );

    if (peerConnectionRef.current) {
      log("🧹", "Closing existing peer connection");
      peerConnectionRef.current.close();
    }

    remoteDescriptionSetRef.current = false;
    pendingIceCandidatesRef.current = [];

    const pc = new RTCPeerConnection(ICE_SERVERS);

    pc.onicecandidate = (e) => {
      if (e.candidate && socket) {
        log("🧊", "Sending ICE candidate to", targetUserId);
        socket.emit("call:iceCandidate", {
          callId,
          targetUserId,
          candidate: e.candidate,
        });
      } else if (!e.candidate) {
        log("🧊", "ICE gathering complete");
      }
    };

    pc.ontrack = (event) => {
      log(
        "📹",
        "Remote track received:",
        event.track.kind,
        "streams:",
        event.streams.length
      );

      if (event.streams?.[0]) {
        const stream = event.streams[0];

        // Log all tracks
        stream.getTracks().forEach((track) => {
          log(
            "🎵",
            `Track: ${track.kind}, enabled: ${track.enabled}, muted: ${track.muted}, readyState: ${track.readyState}`
          );
        });

        remoteStreamRef.current = stream;
        setRemoteStream(stream);

        // Setup audio playback
        setupRemoteAudio(stream);

        setCallStatus("connected");
        stopRingtone();

        log(
          "✅",
          "Remote stream set with",
          stream.getTracks().length,
          "tracks"
        );
      }
    };

    pc.oniceconnectionstatechange = () => {
      log("🧊", "ICE state:", pc.iceConnectionState);
      if (["connected", "completed"].includes(pc.iceConnectionState)) {
        setCallStatus("connected");
        stopRingtone();
      } else if (pc.iceConnectionState === "failed") {
        log("❌", "ICE connection failed");
        endCall("connection_failed");
      } else if (pc.iceConnectionState === "disconnected") {
        log("⚠️", "ICE disconnected, waiting for reconnection...");
        setTimeout(() => {
          if (
            peerConnectionRef.current?.iceConnectionState === "disconnected"
          ) {
            log("❌", "ICE still disconnected, ending call");
            endCall("connection_failed");
          }
        }, 5000);
      }
    };

    pc.onconnectionstatechange = () => {
      log("🔌", "Connection state:", pc.connectionState);
      if (pc.connectionState === "connected") {
        setCallStatus("connected");
        stopRingtone();
      } else if (["failed", "closed"].includes(pc.connectionState)) {
        log("❌", "Connection failed/closed");
        endCall("connection_failed");
      }
    };

    pc.onsignalingstatechange = () => {
      log("🔄", "Signaling state:", pc.signalingState);
    };

    pc.onicegatheringstatechange = () => {
      log("🧊", "ICE gathering state:", pc.iceGatheringState);
    };

    peerConnectionRef.current = pc;
    return pc;
  };

  const getUserMedia = async (type) => {
    try {
      log("🎤", "Requesting media:", type);
      const constraints = {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 48000,
        },
        video:
          type === "video"
            ? {
                width: { ideal: 1280 },
                height: { ideal: 720 },
                facingMode: "user",
              }
            : false,
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      // Verify tracks
      log(
        "✅",
        "Got media tracks:",
        stream.getTracks().map((t) => `${t.kind}:${t.enabled}`)
      );

      const audioTracks = stream.getAudioTracks();
      if (audioTracks.length === 0) {
        throw new Error("No audio track received");
      }

      // ✅ CRITICAL: Set both ref and state
      localStreamRef.current = stream;

      // Force a small delay to ensure state updates properly
      await new Promise((resolve) => setTimeout(resolve, 100));

      setLocalStream(stream);

      log("✅", "Local stream set in state", {
        videoTracks: stream.getVideoTracks().length,
        audioTracks: stream.getAudioTracks().length,
      });

      return stream;
    } catch (e) {
      log("❌", "getUserMedia failed", e);
      alert(`Camera/microphone access failed: ${e.message}`);
      throw e;
    }
  };

  const initiateCall = async (chatId, type, otherUser) => {
    log("📞", "Initiating call to", otherUser.username, "type:", type);
    if (!socket) return alert("Socket not connected");

    try {
      setCallStatus("connecting");

      // Get media first
      const stream = await getUserMedia(type);

      // ✅ CRITICAL: Set state and ref immediately
      localStreamRef.current = stream;
      setLocalStream(stream);

      // Small delay to ensure state propagates
      await new Promise((resolve) => setTimeout(resolve, 150));

      log("✅", "Local stream ready", {
        hasStream: !!stream,
        videoTracks: stream.getVideoTracks().length,
        audioTracks: stream.getAudioTracks().length,
        stateHasStream: !!localStreamRef.current,
      });

      socket.emit("call:invite", { chatId, type }, (res) => {
        log("📤", "Invite response:", res);
        if (res.success) {
          const { callId } = res;
          setActiveCall({
            callId,
            chatId,
            type,
            isInitiator: true,
            otherUser,
            startTime: Date.now(),
          });
          setCallStatus("ringing");
          playRingtone();

          log("✅", "Call initiated, waiting for acceptance");
        } else {
          log("❌", "Invite failed", res.error);
          stopLocalStream();
          setCallStatus("idle");
          alert(`Call failed: ${res.error}`);
        }
      });
    } catch (e) {
      log("❌", "initiateCall error", e);
      stopLocalStream();
      setCallStatus("idle");
    }
  };

  // 🧠 Create & send offer (INITIATOR ONLY)
  const createOfferAndSend = async (callId, targetUserId) => {
    try {
      const pc = peerConnectionRef.current;
      const stream = localStreamRef.current;

      if (!pc || !stream) {
        log("❌", "Missing PC or stream for offer");
        return;
      }

      if (pc.signalingState !== "stable") {
        log("⚠️", "Skipping createOffer — not stable:", pc.signalingState);
        return;
      }

      log("📤", "Creating offer with", stream.getTracks().length, "tracks");

      const offer = await pc.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: activeCallRef.current?.type === "video",
      });

      await pc.setLocalDescription(offer);
      log("✅", "Local description set (offer)");

      socket.emit("call:offer", { callId, targetUserId, sdp: offer });
      log("✅", "Offer sent to", targetUserId);
    } catch (e) {
      log("❌", "createOfferAndSend error", e);
      endCall("error");
    }
  };

  // ✅ Accept incoming call (RECEIVER ONLY)
  const acceptCall = async () => {
    if (!incomingCall) return;
    log("✅", "Accepting call", incomingCall.callId);
    stopRingtone();

    try {
      setCallStatus("connecting");
      const stream = await getUserMedia(incomingCall.type);

      setActiveCall({
        callId: incomingCall.callId,
        chatId: incomingCall.chatId,
        type: incomingCall.type,
        isInitiator: false,
        otherUser: incomingCall.from,
        startTime: Date.now(),
      });

      // Create peer connection and add tracks
      const pc = createPeerConnection(
        incomingCall.callId,
        incomingCall.from._id,
        false
      );

      stream.getTracks().forEach((t) => {
        log("➕", "Adding local track:", t.kind, "enabled:", t.enabled);
        pc.addTrack(t, stream);
      });

      // Verify tracks were added
      const senders = pc.getSenders();
      log("✅", "Total senders:", senders.length);
      senders.forEach((sender) => {
        if (sender.track) {
          log(
            "📡",
            "Sender track:",
            sender.track.kind,
            "enabled:",
            sender.track.enabled
          );
        }
      });

      // Notify the caller that we've accepted
      socket.emit("call:accepted", {
        callId: incomingCall.callId,
        userId: incomingCall.from._id,
      });

      log("✅", "Call accepted, waiting for offer from initiator");
      setIncomingCall(null);
    } catch (e) {
      log("❌", "acceptCall error", e);
      rejectCall();
    }
  };

  const rejectCall = () => {
    log("❌", "Rejecting call");
    stopRingtone();
    if (incomingCall) {
      socket.emit("call:hangup", {
        callId: incomingCall.callId,
        reason: "rejected",
      });
      setIncomingCall(null);
      setCallStatus("idle");
    }
  };

  const endCall = (reason = "user_hangup") => {
    log("📞", "Ending call", reason);
    stopRingtone();

    if (activeCall && socket) {
      socket.emit("call:hangup", { callId: activeCall.callId, reason });
    }

    stopLocalStream();

    // Clean up remote audio
    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null;
      remoteAudioRef.current.pause();
      remoteAudioRef.current.remove();
      remoteAudioRef.current = null;
    }

    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }

    remoteDescriptionSetRef.current = false;
    pendingIceCandidatesRef.current = [];
    remoteStreamRef.current = null;

    setActiveCall(null);
    setIncomingCall(null);
    setCallStatus("idle");
    setRemoteStream(null);
    setIsMuted(false);
    setIsVideoOff(false);
  };

  const stopLocalStream = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => {
        t.stop();
        log("🛑", "Stopped track:", t.kind);
      });
      localStreamRef.current = null;
    }
    setLocalStream(null);
  };

  // 🔌 Socket listeners
  useEffect(() => {
    if (!socket) return;
    log("🧩", "Setting up socket listeners");

    socket.on("incomingCall", (data) => {
      log("📞", "Incoming call", data);
      setIncomingCall(data);
      setCallStatus("ringing");
      playRingtone();
      showCallNotification(data.from, data.type);
    });

    // CALLER receives this after RECEIVER accepts
    socket.on("call:accepted", async ({ callId, userId: accepterId }) => {
      log("✅", "Call accepted by receiver", { callId, accepterId });

      const ac = activeCallRef.current;

      if (!ac || ac.callId !== callId || !ac.isInitiator) {
        log("⚠️", "Ignoring call:accepted — not initiator or wrong call");
        return;
      }

      const stream = localStreamRef.current;
      if (!stream) {
        log("❌", "No local stream available");
        return;
      }

      // Create the peer connection
      log("🚀", "Creating peer connection after acceptance");
      const pc = createPeerConnection(callId, accepterId, true);

      // Add local tracks with proper configuration
      stream.getTracks().forEach((t) => {
        log("➕", "Adding local track:", t.kind, "enabled:", t.enabled);
        const sender = pc.addTrack(t, stream);
        log("✅", "Track added, sender:", sender ? "yes" : "no");
      });

      // Verify tracks
      const senders = pc.getSenders();
      log("✅", "Total senders:", senders.length);

      // Wait for tracks to be ready
      await new Promise((r) => setTimeout(r, 300));

      // Create and send offer
      log("🚀", "Creating and sending offer");
      createOfferAndSend(callId, accepterId);
    });

    // RECEIVER receives offer from CALLER
    socket.on("call:offer", async ({ callId, from, sdp }) => {
      log("📥", "Offer received", { callId, from });
      const pc = peerConnectionRef.current;
      const ac = activeCallRef.current;

      if (!pc || !ac || ac.isInitiator) {
        log("⚠️", "Ignoring offer — no PC, no active call, or we're initiator");
        return;
      }

      // Wait for stable state
      let attempts = 0;
      while (pc.signalingState !== "stable" && attempts < 20) {
        log("⏳", "Waiting for stable state, current:", pc.signalingState);
        await new Promise((r) => setTimeout(r, 100));
        attempts++;
      }

      try {
        await pc.setRemoteDescription(new RTCSessionDescription(sdp));
        log("✅", "Remote description set (offer)");
        remoteDescriptionSetRef.current = true;

        // Process pending ICE candidates
        await processPendingIceCandidates();

        // Create answer
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        log("✅", "Local description set (answer)");

        socket.emit("call:answer", { callId, targetUserId: from, sdp: answer });
        log("✅", "Answer sent to", from);
      } catch (e) {
        log("❌", "Error handling offer", e);
        endCall("error");
      }
    });

    // CALLER receives answer from RECEIVER
    socket.on("call:answer", async ({ callId, from, sdp }) => {
      log("📥", "Answer received", { callId, from });
      const pc = peerConnectionRef.current;
      const ac = activeCallRef.current;

      if (!pc || !ac || !ac.isInitiator) {
        log("⚠️", "Ignoring answer — no PC, no active call, or not initiator");
        return;
      }

      // Wait for proper state
      let attempts = 0;
      while (
        !["have-local-offer", "stable"].includes(pc.signalingState) &&
        attempts < 20
      ) {
        log(
          "⏳",
          "Waiting for proper state for answer, current:",
          pc.signalingState
        );
        await new Promise((r) => setTimeout(r, 100));
        attempts++;
      }

      try {
        await pc.setRemoteDescription(new RTCSessionDescription(sdp));
        log("✅", "Remote description set (answer)");
        remoteDescriptionSetRef.current = true;

        // Process pending ICE candidates
        await processPendingIceCandidates();
      } catch (e) {
        log("❌", "Failed to set remote description (answer)", e);
        endCall("error");
      }
    });

    socket.on("call:iceCandidate", async ({ callId, from, candidate }) => {
      const pc = peerConnectionRef.current;

      if (!pc) {
        log("⚠️", "No PC yet, buffering ICE candidate");
        pendingIceCandidatesRef.current.push(candidate);
        return;
      }

      if (remoteDescriptionSetRef.current) {
        try {
          await pc.addIceCandidate(new RTCIceCandidate(candidate));
          log("✅", "ICE candidate added");
        } catch (e) {
          log("⚠️", "Error adding ICE", e);
        }
      } else {
        log("🕓", "Buffering ICE candidate (remoteDescription not ready)");
        pendingIceCandidatesRef.current.push(candidate);
      }
    });

    socket.on("call:ended", ({ callId, reason, by }) => {
      log("📞", "Call ended", { callId, reason, by });
      endCall(reason);
    });

    return () => {
      log("🧹", "Cleaning socket listeners");
      socket.off("incomingCall");
      socket.off("call:accepted");
      socket.off("call:offer");
      socket.off("call:answer");
      socket.off("call:iceCandidate");
      socket.off("call:ended");
    };
  }, [socket]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = null;
        remoteAudioRef.current.remove();
      }
    };
  }, []);

  return (
    <CallContext.Provider
      value={{
        incomingCall,
        activeCall,
        callStatus,
        localStream,
        remoteStream,
        isMuted,
        isVideoOff,
        initiateCall,
        acceptCall,
        rejectCall,
        endCall,
        toggleMute: () => {
          const track = localStreamRef.current?.getAudioTracks()[0];
          if (track) {
            track.enabled = !track.enabled;
            setIsMuted(!track.enabled);
            log("🔇", "Mute toggled:", !track.enabled);
          }
        },
        toggleVideo: () => {
          const track = localStreamRef.current?.getVideoTracks()[0];
          if (track) {
            track.enabled = !track.enabled;
            setIsVideoOff(!track.enabled);
            log("📹", "Video toggled:", !track.enabled);
          }
        },
      }}
    >
      {children}
    </CallContext.Provider>
  );
};

export const useCall = () => {
  const ctx = useContext(CallContext);
  if (!ctx) throw new Error("useCall must be used within CallProvider");
  return ctx;
};
